import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, Search, FolderOpen, Zap, LayoutGrid, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusOf, statusKey, fmt } from "@/lib/catalog";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { tr, locale } from "@/lib/i18n";
const STATUS_STYLE = {
  Bozza: "bg-slate-100 text-slate-600",
  "In corso": "bg-blue-50 text-[#0F52BA]",
  Completato: "bg-emerald-50 text-emerald-700",
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-4">
    <span className="w-10 h-10 rounded-md bg-blue-50 text-[#0F52BA] grid place-items-center"><Icon className="w-5 h-5" /></span>
    <div>
      <div className="text-xs font-tech uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-display font-semibold text-slate-900" data-testid={testid}>{value}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  const load = () => api.get("/projects").then(({ data }) => setProjects(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/projects", {});
    nav(`/progetti/${data.id}`);
  };
  const duplicate = async (id) => {
    await api.post(`/projects/${id}/duplicate`);
    toast.success(tr("Progetto duplicato"));
    load();
  };
  const remove = async () => {
    await api.delete(`/projects/${toDelete.id}`);
    toast.success(tr("Progetto eliminato"));
    setToDelete(null);
    load();
  };

  const list = projects.filter((p) =>
    [p.name, p.client, p.address].join(" ").toLowerCase().includes(q.toLowerCase()));
  const totalKwp = projects.reduce((s, p) => s + (p.summary?.kwp || 0), 0);
  const totalMod = projects.reduce((s, p) => s + (p.summary?.modules || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader>
        <Button data-testid="new-project-btn" onClick={create} className="h-10 bg-[#0F52BA] hover:bg-[#0D47A1]">
          <Plus className="w-4 h-4 mr-1.5" /> {tr("Nuovo progetto")}
        </Button>
      </AppHeader>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        <div className="sr-rise">
          <div className="text-xs font-tech uppercase tracking-wider text-[#0F52BA] mb-2">{tr("Dashboard progetti")}</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-slate-900">{user.role === "admin" ? tr("Tutti gli impianti") : user.group_name ? tr("Impianti di {0}", [user.group_name]) : tr("I tuoi impianti")}</h1>
          <p className="text-slate-500 mt-3 max-w-2xl">{tr("Progetta la struttura di montaggio del fotovoltaico su tetto a falda o piano: dall'indirizzo al report con carichi, layout e distinta dei materiali.")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sr-rise" style={{ animationDelay: "80ms" }}>
          <Stat icon={FolderOpen} label={tr("Progetti")} value={projects.length} testid="stat-projects" />
          <Stat icon={LayoutGrid} label={tr("Moduli totali")} value={totalMod} testid="stat-modules" />
          <Stat icon={Zap} label={tr("Potenza totale")} value={`${fmt(totalKwp)} kWp`} testid="stat-kwp" />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg sr-rise" style={{ animationDelay: "160ms" }}>
          <div className="p-4 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="project-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Cerca per nome, cliente o indirizzo")} className="pl-9 h-10" />
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-400">{tr("Caricamento…")}</div>
          ) : list.length === 0 ? (
            <div className="p-14 text-center" data-testid="projects-empty">
              <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-[#0F52BA] grid place-items-center mb-4"><LayoutGrid className="w-6 h-6" /></div>
              <div className="font-display text-lg font-semibold">{tr("Nessun progetto")}</div>
              <p className="text-slate-500 text-sm mt-1 mb-5">{tr("Crea il primo progetto per iniziare a configurare un impianto.")}</p>
              <Button data-testid="empty-new-project-btn" onClick={create} className="bg-[#0F52BA] hover:bg-[#0D47A1]"><Plus className="w-4 h-4 mr-1.5" />{tr("Nuovo progetto")}</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="projects-table">
                <thead>
                  <tr className="text-left text-xs font-tech uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-5 py-3">{tr("Progetto")}</th><th className="px-5 py-3">{tr("Indirizzo")}</th><th className="px-5 py-3">{tr("Proprietario")}</th><th className="px-5 py-3">{tr("Stato")}</th>
                    <th className="px-5 py-3">{tr("Potenza")}</th><th className="px-5 py-3">{tr("Aggiornato")}</th><th className="px-5 py-3 text-right">{tr("Azioni")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => {
                    const s = statusOf(p);
                    return (
                      <tr key={p.id} data-testid={`project-row-${p.id}`} onClick={() => nav(`/progetti/${p.id}`)} className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900">{p.name}</div>
                          <div className="text-slate-500 text-xs">{p.client || tr("Nessun cliente")}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-600 max-w-xs"><div className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" /><span className="line-clamp-2">{p.address || "—"}</span></div></td>
                        <td className="px-5 py-4 text-slate-600 whitespace-nowrap" data-testid={`project-owner-${p.id}`}>{p.owner_id === user.id ? tr("Tu") : p.owner_name || "—"}</td>
                        <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLE[statusKey(p)]}`}>{s}</span></td>
                        <td className="px-5 py-4 font-tech text-slate-700">{fmt(p.summary?.kwp)} kWp</td>
                        <td className="px-5 py-4 text-slate-500">{new Date(p.updated_at).toLocaleDateString(locale())}</td>
                        <td className="px-5 py-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" title={tr("Duplica")} data-testid={`duplicate-project-${p.id}`} onClick={() => duplicate(p.id)}><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" title={tr("Elimina")} data-testid={`delete-project-${p.id}`} onClick={() => setToDelete(p)} className="hover:text-amber-700"><Trash2 className="w-4 h-4" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{tr("Eliminare il progetto?")}</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}{tr("\" verrà eliminato definitivamente.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">{tr("Annulla")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={remove} className="bg-amber-600 hover:bg-amber-700">{tr("Elimina")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
